import React, { useMemo } from 'react';

import { DisclaimerConfiguration, InventoryDisclaimer } from '../types/main';
import { getDisclaimersByPage } from '../utils/disclaimer';
import { useSafeContext } from './use-safe-context';

export const DisclaimerConfigurationContext = React.createContext<
  DisclaimerConfiguration | undefined
>(undefined);

export function useDisclaimerConfiguration(): DisclaimerConfiguration {
  const disclaimerConfiguration = useSafeContext(
    DisclaimerConfigurationContext,
    'disclaimerConfiguration',
  );

  return disclaimerConfiguration;
}

/**
 * Returns all disclaimer numbers for a given model and page, in API order.
 * Use this when a vehicle can appear in multiple disclaimers (e.g. IIHS + price).
 */
export const useDisclaimerKeysByModelAndPage = (
  pageName: string,
  modelKey?: string,
): (string | undefined)[] => {
  const { disclaimers } = useDisclaimerConfiguration();

  return useMemo(() => {
    if (!modelKey || !pageName || !disclaimers) return [];

    const filteredDisclaimers = getDisclaimersByPage(pageName, disclaimers);
    return (
      filteredDisclaimers
        ?.filter((disclaimer: InventoryDisclaimer) =>
          disclaimer.models.includes(modelKey),
        )
        .map((disclaimer: InventoryDisclaimer) => disclaimer.number) ?? []
    );
  }, [pageName, modelKey, disclaimers]);
};

/**
 * Hook to get a specific disclaimer key
 * @param pageName
 * @param modelKey
 */
export const useDisclaimerKeyByModelAndPage = (
  pageName: string,
  modelKey?: string,
): string | undefined => {
  const keys = useDisclaimerKeysByModelAndPage(pageName, modelKey);
  return keys[0];
};
